import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { IconPlus, IconTrash, IconGripVertical } from '@tabler/icons-react'
import { useState } from 'react'
import { StageActions } from './stage-actions'
import type { Stage, StageAction } from '../../schema'

interface StagesBuilderProps {
  stages: Stage[]
  onChange: (stages: Stage[]) => void
}

export function StagesBuilder({ stages, onChange }: StagesBuilderProps) {
  const [expandedStage, setExpandedStage] = useState<string | null>(null)

  const addStage = () => {
    const newStage: Stage = {
      id: `stage-${Date.now()}`,
      name: 'Novo Estágio',
      instructions: '',
      triggers: [],
      actions: [],
    }
    onChange([...stages, newStage])
    setExpandedStage(newStage.id)
  }

  const updateStage = (id: string, updates: Partial<Stage>) => {
    onChange(
      stages.map((stage) =>
        stage.id === id ? { ...stage, ...updates } : stage
      )
    )
  }

  const deleteStage = (id: string) => {
    onChange(stages.filter((stage) => stage.id !== id))
    if (expandedStage === id) {
      setExpandedStage(null)
    }
  }

  const addTrigger = (stageId: string) => {
    const stage = stages.find((s) => s.id === stageId)
    if (stage) {
      updateStage(stageId, {
        triggers: [...stage.triggers, ''],
      })
    }
  }

  const updateTrigger = (
    stageId: string,
    triggerIndex: number,
    value: string
  ) => {
    const stage = stages.find((s) => s.id === stageId)
    if (stage) {
      const newTriggers = [...stage.triggers]
      newTriggers[triggerIndex] = value
      updateStage(stageId, { triggers: newTriggers })
    }
  }

  const removeTrigger = (stageId: string, triggerIndex: number) => {
    const stage = stages.find((s) => s.id === stageId)
    if (stage) {
      updateStage(stageId, {
        triggers: stage.triggers.filter((_, i) => i !== triggerIndex),
      })
    }
  }

  const updateActions = (stageId: string, actions: StageAction[]) => {
    updateStage(stageId, { actions })
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <p className='text-sm text-muted-foreground'>
          {stages.length === 0
            ? 'Nenhum estágio criado ainda.'
            : `${stages.length} estágio(s) configurado(s)`}
        </p>
        <Button type='button' variant='outline' size='sm' onClick={addStage}>
          <IconPlus className='w-4 h-4 mr-2' />
          Adicionar Estágio
        </Button>
      </div>

      <div className='space-y-3'>
        {stages.map((stage, index) => (
          <Card key={stage.id}>
            <CardHeader
              className='cursor-pointer hover:bg-muted/50 transition-colors'
              onClick={() =>
                setExpandedStage(expandedStage === stage.id ? null : stage.id)
              }
            >
              <div className='flex items-center gap-3'>
                <IconGripVertical className='w-4 h-4 text-muted-foreground' />
                <CardTitle className='text-base flex-1'>
                  {index + 1}. {stage.name || 'Sem nome'}
                </CardTitle>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  onClick={(e) => {
                    e.stopPropagation()
                    deleteStage(stage.id)
                  }}
                >
                  <IconTrash className='w-4 h-4 text-destructive' />
                </Button>
              </div>
            </CardHeader>

            {expandedStage === stage.id && (
              <CardContent className='space-y-4 pt-0'>
                <div className='space-y-2'>
                  <Label>Nome do Estágio</Label>
                  <Input
                    value={stage.name}
                    onChange={(e) =>
                      updateStage(stage.id, { name: e.target.value })
                    }
                    placeholder='Ex: Agendamento, Consulta API, Transbordo'
                  />
                </div>

                <div className='space-y-2'>
                  <Label>Instruções do Estágio</Label>
                  <Textarea
                    value={stage.instructions}
                    onChange={(e) =>
                      updateStage(stage.id, { instructions: e.target.value })
                    }
                    placeholder='Instruções específicas para este estágio...'
                    rows={4}
                  />
                </div>

                <div className='space-y-2'>
                  <div className='flex items-center justify-between'>
                    <Label>Gatilhos (Triggers)</Label>
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => addTrigger(stage.id)}
                    >
                      <IconPlus className='w-3 h-3 mr-1' />
                      Adicionar Gatilho
                    </Button>
                  </div>

                  {stage.triggers.length === 0 ? (
                    <p className='text-sm text-muted-foreground'>
                      Nenhum gatilho configurado.
                    </p>
                  ) : (
                    <div className='space-y-2'>
                      {stage.triggers.map((trigger, triggerIndex) => (
                        <div key={triggerIndex} className='flex gap-2'>
                          <Input
                            value={trigger}
                            onChange={(e) =>
                              updateTrigger(
                                stage.id,
                                triggerIndex,
                                e.target.value
                              )
                            }
                            placeholder='Ex: usuário menciona agendamento'
                          />
                          <Button
                            type='button'
                            variant='ghost'
                            size='icon'
                            onClick={() => removeTrigger(stage.id, triggerIndex)}
                          >
                            <IconTrash className='w-4 h-4 text-destructive' />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className='pt-2 border-t'>
                  <StageActions
                    actions={stage.actions || []}
                    onChange={(actions) => updateActions(stage.id, actions)}
                  />
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    </div>
  )
}
